import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import {
  COLORS,
  ComponentTextStyle,
  ComponentXXS,
  LinearProgress,
  LinearProgressType,
  LinearProgressVariant,
  Size,
} from '../../index';
import { TextButton, TextButtonProps, IconButton } from '../../Button';

export interface VerticalCardActionItem {
  /** Optional. Variant of the action button, defaults to 'secondary'. */
  variant?: 'primary' | 'secondary';
  /** Icon to be used in the icon button. */
  icon: React.ReactNode;
  /** Click handler for the action button. */
  onClick: () => void;
}

export type VerticalCardStandardButton = Pick<TextButtonProps, 'width' | 'variant' | 'loading' | 'icon' | 'onClick' | 'disabled'> & {
  buttonText: string;
  size?: Size.Small | Size.Medium | Size.Large;
}

export interface VerticalCardBottomSectionProps {
  /** Optional. Current progress level shown in the progress bar. */
  progressLevel?: number;
  /** Optional. Maximum level of progress in the progress bar. */
  progressMax?: number;
  /** Optional. Type of the progress bar. */
  progressType?: LinearProgressType;

  /** Optional. Note text shown on the left side of the section. */
  noteLeft?: string;
  /** Optional. Note icon shown on the left side of the section. */
  noteLeftIcon?: React.ReactNode;
  /** Optional. Note text shown on the right side of the section. */
  noteRight?: string;
  /** Optional. Note icon shown on the right side of the section. */
  noteRightIcon?: React.ReactNode;

  /** Optional. Note icon/Hyperlink/button shown on the left side of the section. */
  leftButton?: VerticalCardStandardButton;

  /** Author name shown at the bottom-left part of the section.  */
  authorName?: string;
  /** Array of actions shown on the bottom-right corner of the section. */
  actions?: VerticalCardActionItem[];
  /** Logo shown on the right side of the section, in front of AuthorName. */
  logo?: React.ReactNode;
  /** This property is set by Card component itself and is based on 'disabled' flag of CardProps. */
  disabled?: boolean;
}

export const VerticalCardBottomSectionProgressStyles = styled.div`
  padding: 8px 0;
`;

export const VerticalCardBottomSectionNotesStyles = styled.div`
  height: 48px;
  position: relative;
  color: ${props => COLORS.generateToken({ componentType:'text', defaultVariant:'subtle' }, props.theme)};
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const VerticalCardBottomSectionAuthorStyles = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 48px;

  div:first-child {
    flex-grow: 2;
  }

  svg, img {
    width: 80px;
    flex-grow: 0;
    flex-shrink: 0;
  }

  ${props => props.$disabled ? `
    color: ${COLORS.generateToken({ componentType: 'text', state: 'disabled' }, props.theme)};
    svg, img {
      filter: grayscale(100%);
    }
    
  ` : ''}
`;

export const VerticalCardBottomSectionDivider = styled.div`
  border-top: 1px;
  border-top-color: ${props => COLORS.generateToken({componentType:'border', defaultVariant:'subtle'}, props.theme)};
  border-top-style: solid;
  width: 100%;
`;

export const VerticalCardBottomSectionNoteLeftStyles = styled.div`
  position: absolute;
  left: 0px;
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    margin-right: 4px;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

export const VerticalCardBottomSectionNoteRightStyles = styled.div`
  position: absolute;
  right: 0px;
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    margin-right: 4px;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

export const VerticalCardBottomSectionButtonRowContainerStyles = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px;
  border-top-color: ${props => COLORS.generateToken({componentType:'border', defaultVariant:'subtle'}, props.theme)};
  border-top-style: solid;

  button:last-child {
    margin: 8px 0px 0px 0px;
  }

  .card-bottom-section-action-button {
    z-index: 2000;
  }

  button:not(:last-child) {
    margin: 8px 0px 0px 0px;
  }

  .card-bottom-section-action-button {
    z-index: 2000;
  }

  svg {
    color: ${props => COLORS.generateToken({componentType:'icon', defaultVariant:'subtle' }, props.theme)};
  }
`;

export const VerticalCardBottomSectionContainer = styled.div`
  padding: 0px 16px 8px 16px;
  width: calc(100% - 32px);
`;

const LeftItemContainer = styled.div`
  flex: 1
`;

const RightItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  color: ${props => COLORS.getColor('neutral_600', props.theme)};
`;

const VerticalCardBottomSection = React.forwardRef(({
  progressLevel,
  progressMax,
  progressType = LinearProgressType.Line,
  noteLeft,
  noteLeftIcon,
  noteRight,
  noteRightIcon,
  leftButton,
  authorName,
  logo,
  actions,
  disabled
}: VerticalCardBottomSectionProps,
  ref: React.Ref<React.RefObject<HTMLButtonElement | null>[]>) => {

  const [elRefs, setElRefs] = React.useState<React.RefObject<HTMLButtonElement | null>[]>([]);
  const theme = useTheme();
  const length = actions?.length || 0;

  React.useEffect(() => {
    setElRefs(Array((length || 0) + (leftButton ? 1 : 0)).fill(null).map(() => React.createRef<HTMLButtonElement>()));
  }, [length, leftButton]);

  React.useImperativeHandle(ref, () => elRefs, [elRefs]);

  const haveAtLeastSomething = progressLevel != undefined || noteLeft || noteLeftIcon || noteRight || noteRightIcon || authorName || leftButton;

  const renderLeftButton = () => {
    if (leftButton){
      const {buttonText, onClick, ...rest} = leftButton;
      return (<TextButton className="card-bottom-section-action-button"
        ref={elRefs[0]}
        size={ leftButton.size && [Size.XSmall, Size.XSmall, Size.XXSmall].includes(leftButton.size) ? Size.Small: 
          leftButton.size == Size.Large ? Size.Large : Size.Medium}
        {...rest}
        onClick={(e) => {
          e.stopPropagation();
          onClick && onClick(e);
        }}>
        {buttonText}
      </TextButton>)
    }
}

  return (
    <VerticalCardBottomSectionContainer data-testid={'card-bottomSection'}>
      {haveAtLeastSomething && <VerticalCardBottomSectionDivider/>}
      {progressLevel != undefined && <VerticalCardBottomSectionProgressStyles>
        <LinearProgress size={Size.Small}
          type={progressType}
          variant={LinearProgressVariant.Normal}
          value={progressLevel}
          max={progressMax ?? progressLevel}/>
      </VerticalCardBottomSectionProgressStyles>}

      {(noteLeft || noteLeftIcon || noteRight || noteRightIcon) && <VerticalCardBottomSectionNotesStyles>
        {(noteLeft || noteLeftIcon) && (
          <VerticalCardBottomSectionNoteLeftStyles>
            {noteLeftIcon}
            <ComponentXXS color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)} textStyle={ComponentTextStyle.Bold}>{noteLeft}</ComponentXXS>
          </VerticalCardBottomSectionNoteLeftStyles>
        )}
        {(noteRight || noteRightIcon) && (
          <VerticalCardBottomSectionNoteRightStyles>
            {noteRightIcon}
            <ComponentXXS color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)} textStyle={ComponentTextStyle.Bold}>{noteRight}</ComponentXXS>
          </VerticalCardBottomSectionNoteRightStyles>
        )}
      </VerticalCardBottomSectionNotesStyles>}

      {(authorName || logo) && <VerticalCardBottomSectionAuthorStyles $disabled={disabled} data-testid={'card-bottomSection-author'}>
        <ComponentXXS>{authorName}</ComponentXXS>
        {logo}
      </VerticalCardBottomSectionAuthorStyles>}

      {(actions || leftButton) && <VerticalCardBottomSectionButtonRowContainerStyles>
        <LeftItemContainer>
          {leftButton && renderLeftButton()}
      </LeftItemContainer>

        { actions && <RightItemContainer>{actions.map((x, index) => (
            <IconButton ref={elRefs[index + (leftButton ? 1 : 0)]}
              className="card-bottom-section-action-button"
              key={index}
              z-index={2000}
              disabled={disabled}
              variant={x.variant ?? "secondary"}
              shape="circular"
              action={(e) => {
                x.onClick();
              }}>
              {x.icon}
            </IconButton>
          ))}
          </RightItemContainer>
        }
      </VerticalCardBottomSectionButtonRowContainerStyles>}
    </VerticalCardBottomSectionContainer>
  );
});

export default VerticalCardBottomSection;
